import { useIntl } from 'react-intl';

import Button, { LinkButton } from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import { Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';

import packageFreeSvg from 'src/components/packages/components/packages-empty-states/assets/packages-unavailable-free.svg';
import {
  ORIGIN,
  PLAN_AND_BILLING_URL,
} from 'src/components/pipelines/constants';
import { useAccount } from 'src/components/pipelines/hooks/account';
import { getSettingsPageURL } from 'src/components/pipelines/utils/links';
import { useRepositoryAdminStatus } from 'src/hooks/use-repository-admin-status';
import { useParams } from 'src/router/hooks';

import ActivateMergeQueueIcon from '../assets/activate-merge-queue.svg';
import PipelinesDisabledIcon from '../assets/enable-pipelines.svg';
import MergeQueuesEmptyIcon from '../assets/merge-queues.svg';
import { messages } from '../merge-queues.i18n';
import { useIsEmpty, useIsLoading, useMergeQueuesActions } from '../provider';
import { MergeQueueActionModalType } from '../types';

import { CreateMergeQueuesButton } from './MergeQueueActions';

const loadingXcss = xcss({
  height: '60%',
});

export const MergeQueuesEmpty = ({
  isRenderedInSettings = false,
}: {
  isRenderedInSettings?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const [{ isEmpty }] = useIsEmpty();
  const [{ isLoading }] = useIsLoading();
  const { repositoryOwner, repositorySlug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();
  const isAdmin = useRepositoryAdminStatus();

  if (isLoading || !isEmpty) return null;

  return (
    <EmptyState
      header={formatMessage(messages.emptyStateHeader)}
      description={
        <>
          {formatMessage(messages.emptyStateDescription)}{' '}
          {!isAdmin && formatMessage(messages.contactAdmin)}
        </>
      }
      imageUrl={MergeQueuesEmptyIcon}
      testId="merge-queues-empty"
      secondaryAction={
        isRenderedInSettings ? (
          <CreateMergeQueuesButton isCentralButton />
        ) : (
          isAdmin && (
            <LinkButton
              appearance="primary"
              href={`${ORIGIN}/${repositoryOwner}/${repositorySlug}/admin/merge-queues/`}
            >
              {formatMessage(messages.createMergeQueue)}
            </LinkButton>
          )
        )
      }
    />
  );
};

export const MergeQueuesLoading = () => {
  const [{ isLoading }] = useIsLoading();

  if (!isLoading) return null;

  return (
    <Flex alignItems="center" justifyContent="center" xcss={loadingXcss}>
      <Spinner size="large" testId="merge-queues-spinner" />
    </Flex>
  );
};

export const MergeQueueInactive = () => {
  const { formatMessage } = useIntl();

  const [, { setMergeQueueActionModal }] = useMergeQueuesActions();

  return (
    <EmptyState
      header={formatMessage(messages.inactiveStateHeader)}
      description={formatMessage(messages.inactiveStateDescription)}
      imageUrl={ActivateMergeQueueIcon}
      testId="merge-queues-inactive"
      secondaryAction={
        <Button
          onClick={() =>
            setMergeQueueActionModal(MergeQueueActionModalType.ACTIVATE)
          }
        >
          {formatMessage(messages.activateMergeQueue)}
        </Button>
      }
    />
  );
};

export const MergeQueuesPipelinesDisabled = () => {
  const { addonKey } = useAccount();
  const { formatMessage } = useIntl();
  const { repositoryOwner, repositorySlug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();

  return (
    <EmptyState
      header={formatMessage(messages.pipelinesDisabledTitle)}
      description={formatMessage(messages.pipelinesDisabledDescription)}
      imageUrl={PipelinesDisabledIcon}
      maxImageHeight={212}
      maxImageWidth={280}
      testId="merge-queues-pipelines-disabled"
      secondaryAction={
        <LinkButton
          appearance="primary"
          href={getSettingsPageURL(
            `${repositoryOwner}/${repositorySlug}`,
            addonKey
          )}
        >
          {formatMessage(messages.enablePipelines)}
        </LinkButton>
      }
    />
  );
};

export const MergeQueuesUnavailable = () => {
  const { formatMessage } = useIntl();

  return (
    <EmptyState
      header={formatMessage(messages.paidPlanRequiredHeader)}
      description={formatMessage(messages.paidPlanRequiredDescription)}
      imageUrl={packageFreeSvg}
      primaryAction={
        <LinkButton appearance="primary" href={PLAN_AND_BILLING_URL}>
          {formatMessage(messages.explorePlans)}
        </LinkButton>
      }
    />
  );
};
